import PropTypes from 'prop-types'
import styled from 'astroturf/react'
import { PaymentAmount } from '@calendly/booking-kit'
const colorTextSuccess = 'var(--colorTextSuccess, var(--coreColorGreen800))'
const colorTextWarning = 'var(--colorTextWarning, var(--coreColorOrange700))'
import { IconButton } from '@calendly/ui/components/icon-button'
import { IconX } from '@calendly/ui/components/glyphs/IconX'
import { useSelector } from 'react-redux'
import { useEffect } from 'react'

import { useValidateCouponMutation } from 'root-api'
import Info from 'components/enter_details/payment/info'
import { useI18n } from 'locales'
import { useTrackCommerce } from 'hooks/useTrackCommerce'
import { anonymousIdSelector } from 'selectors/segment'
import { useEventType } from 'components/use_event_type'
import { couponErrorSelector } from 'selectors/payment'
import { toLowerCase } from 'utils/strings'

import { couponErrorAnalyticsText } from './coupon'
import {
  MINIMUM_CHARGE_CENTS,
  computeFinalPayment,
  formatCents,
  minorUnitExponent,
  parseAmountFromSymbol,
  symbolFromPattern
} from './booking_code_utils'
import _PaymentTerms from "info_coupon-PaymentTerms.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=PaymentTerms";
import _LineItem from "info_coupon-LineItem.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=LineItem";
import _BonusText from "info_coupon-BonusText.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=BonusText";
import _LimitedCouponText from "info_coupon-LimitedCouponText.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=LimitedCouponText";
import _CouponAmountContainer from "info_coupon-CouponAmountContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=CouponAmountContainer";
import _RemoveCouponButton from "info_coupon-RemoveCouponButton.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=RemoveCouponButton";


const PaymentTerms = /*#__PURE__*/styled('div', null, {
  displayName: "PaymentTerms",
  styles: _PaymentTerms
})

const LineItem = /*#__PURE__*/styled('div', null, {
  displayName: "LineItem",
  styles: _LineItem
})

const BonusText = /*#__PURE__*/styled('span', null, {
  displayName: "BonusText",
  styles: _BonusText
})

const LimitedCouponText = /*#__PURE__*/styled('span', null, {
  displayName: "LimitedCouponText",
  styles: _LimitedCouponText
})

const CouponAmountContainer = /*#__PURE__*/styled('div', null, {
  displayName: "CouponAmountContainer",
  styles: _CouponAmountContainer
})

const RemoveCouponButton = /*#__PURE__*/styled(IconButton, null, {
  displayName: "RemoveCouponButton",
  styles: _RemoveCouponButton,
  attrs: props => ({
    ...props,
    icon: IconX,
    size: 'small'
  })
})

const couponValueText = (coupon, amountWithSymbol, currency, i18n) => {
  if (!coupon || !amountWithSymbol) return undefined

  if (coupon.fixed_amount_cents) {
    const discount = formatCents(coupon.fixed_amount_cents, parseAmountFromSymbol(amountWithSymbol), minorUnitExponent(currency), currency)
    return `${discount} ${i18n.t('booking.templates.payment.coupon.off')}`
  }
  if (coupon.percent_off) {
    return `${(coupon.percent_off).toFixed(0)}% ${i18n.t('booking.templates.payment.coupon.off')}`
  }
  return undefined
}

const limitedCouponValue = (payment, currency, i18n) => {
  const { finalCents, parsed, exponent } = payment
  // Don't show limited coupon warning for $0 amounts
  if (finalCents === 0) {
    return undefined
  }
  if (finalCents === MINIMUM_CHARGE_CENTS) {
    const symbol = symbolFromPattern(parsed.formatPattern)
    // The locale template renders "%{symbol}%{min_charge_amount}", so suffix formats ("0,50 €")
    // carry their symbol inside the amount to match the Final Price line.
    return i18n.t('booking.templates.payment.coupon.limited', {
      min_charge_amount: formatCents(MINIMUM_CHARGE_CENTS, symbol ? { ...parsed, formatPattern: '$1' } : parsed, exponent, currency),
      symbol
    })
  }
  return undefined
}

const InfoCoupon = ({
  amountWithSymbol,
  amountCents,
  currency,
  onRemoveCoupon,
  paymentTerms = null
}) => {
  const { i18n } = useI18n()
  const [, { data: {
    resource: coupon
  }, reset }
  ] = useValidateCouponMutation({
    fixedCacheKey: 'couponData'
  })
  const track = useTrackCommerce()
  const anonymousId = useSelector(anonymousIdSelector)
  const couponSubmissionError = useSelector(couponErrorSelector)
  const eventType = useEventType()

  useEffect(() => {
    if (couponSubmissionError.error) {
      track('Booking Code Interaction',
        {
          invitee_anonymous_id: anonymousId,
          event_type_uuid: eventType.uuid,
          action: 'apply',
          booking_code_valid: false,
          booking_code: coupon.booking_code,
          discount_type: toLowerCase(coupon.booking_code_type),
          fixed_amount_cents: coupon.fixed_amount_cents,
          percent_off: coupon.percent_off,
          currency: coupon.currency,
          error_reason: couponErrorAnalyticsText(couponSubmissionError.data),
        })
      reset()
      onRemoveCoupon()
    }
  }, [couponSubmissionError, track, anonymousId, eventType, coupon, onRemoveCoupon, reset])

  if (!coupon) {
    return (
      <Info
        amountWithSymbol={amountWithSymbol}
        currency={currency}
        paymentTerms={paymentTerms}
      />
    )
  }


  const removeCoupon = () => {
    track('Booking Code Interaction',
      {
        invitee_anonymous_id: anonymousId,
        event_type_uuid: eventType.uuid,
        action: 'remove',
        booking_code_valid: null,
        booking_code: coupon.booking_code,
        discount_type: toLowerCase(coupon.booking_code_type),
        fixed_amount_cents: coupon.fixed_amount_cents,
        percent_off: coupon.percent_off,
        currency: coupon.currency,
      })
    reset()
    onRemoveCoupon()
  }

  const payment = computeFinalPayment(amountWithSymbol, coupon, { amountCents, currency })
  const limitedText = limitedCouponValue(payment, currency, i18n)

  return (
    <div>
      <LineItem>
        {i18n.t('booking.templates.payment.coupon.original_price')}
        <PaymentAmount
          amount={amountWithSymbol}
          currencyCode={currency}
        />
      </LineItem>
      <LineItem>
        <BonusText>{`${coupon.description} (${coupon.code})`}</BonusText>
        <CouponAmountContainer>
          <BonusText>{couponValueText(coupon, amountWithSymbol, currency, i18n)}</BonusText>
          <RemoveCouponButton
            aria-label={i18n.t('action.remove')}
            onClick={removeCoupon}
          />
        </CouponAmountContainer>
      </LineItem>
      {limitedText && (
        <LineItem>
          <LimitedCouponText>{limitedText}</LimitedCouponText>
        </LineItem>
      )}
      {Number.isFinite(payment.finalCents) && (
        <LineItem>
          <strong>{i18n.t('booking.templates.payment.coupon.final_price')}</strong>
          <PaymentAmount
            amount={payment.formatted}
            currencyCode={currency}
          />
        </LineItem>
      )}
      {paymentTerms && (
        <PaymentTerms>
          <strong>{i18n.t('booking.templates.payment.payment_terms')}</strong>
          <div>{paymentTerms}</div>
        </PaymentTerms>
      )}
    </div>
  )
}

InfoCoupon.propTypes = {
  amountWithSymbol: PropTypes.string.isRequired,
  amountCents: PropTypes.number,
  currency: PropTypes.string.isRequired,
  paymentTerms: PropTypes.string,
  onRemoveCoupon: PropTypes.func.isRequired,
}

export default InfoCoupon
